"use client";
import React, { useState, useEffect } from "react";
import { NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaDownload,
} from "react-icons/fa";
import { LoadingTable } from "../components/LoadingSpinner";
import { exportToCSVWithHeaders } from "@/lib/csvExport";

interface ProteinData {
  protein_accession: string;
  protein_name: string;
  strain: string;
  Antigen_Name: string;
  Antigen_Status?: string;
  location_name?: string;
  cellular_component?: string;
  biological_process?: string;
  molecular_function?: string;
  pdb_id?: string;
  predicted_structure?: string;
  cluster_id?: string;
}

const ProteinPage: NextPage = () => {
  // Get query parameters
  const searchParams = useSearchParams();
  const epitopeParam = searchParams.get("epitope");
  
  // Data states
  const [filteredProteins, setFilteredProteins] = useState<ProteinData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Search states
  const [proteinNameSearch, setProteinNameSearch] = useState<string>("");
  const [strainSearch, setStrainSearch] = useState<string>("");
  
  // Filter states
  const [antigenFilter, setAntigenFilter] = useState<string>("");
  const [hasAntigenOnly, setHasAntigenOnly] = useState<boolean>(false);
  const [locationFilter, setLocationFilter] = useState<string>("");
  const [functionFilter, setFunctionFilter] = useState<string>("");
  const [clusterFilter, setClusterFilter] = useState<string>("");
  
  // Table states
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortField, setSortField] = useState<keyof ProteinData>("protein_accession");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  
  // Unique values for filters
  const [uniqueAntigens, setUniqueAntigens] = useState<string[]>([]);
  const [uniqueLocations, setUniqueLocations] = useState<string[]>([]);
  const [uniqueFunctions, setUniqueFunctions] = useState<string[]>([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ page: 0, total: 0 });

const [initialFilterLoaded, setInitialFilterLoaded] = useState(false);

  // เพิ่ม state
  const [aspectFilter, setAspectFilter] = useState<string>("molecular function");
  const [aspectOptions] = useState<string[]>([
    "cellular component",
    "biological process",
    "molecular function",
  ]);

  // Fetch filter options (antigens, locations, functions)
  const fetchFilterOptions = async () => {
    setFilterOptionsLoading(true);
    try {
      const response = await fetch("/api/get-protein-filter-options", {
        method: "get",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const result = await response.json();

        const expandedLocations = (result.locations || [])
        .flatMap((loc: string) =>
          loc.split(";").map((l) => l.trim()).filter((l) => l !== "")
        );

        const uniqueExpanded = Array.from(new Set(expandedLocations));

        setUniqueAntigens(result.antigens || []);
        setUniqueLocations(uniqueExpanded.sort());
        setUniqueFunctions(result.functions || []);
      } else {
        console.error("Failed to fetch filter options");
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  // ปรับ fetchFilterOptions ให้รับ aspect
  const fetchFunctionOptions = async (aspect: string) => {
    setFilterOptionsLoading(true);
    try {
      const response = await fetch(`/api/get-protein-filter-options?aspect=${encodeURIComponent(aspect)}`);
      if (response.ok) {
        const result = await response.json();
        setUniqueFunctions(result.functions || []);
      } else {
        console.error("Failed to fetch function options");
      }
    } catch (error) {
      console.error("Error fetching function options:", error);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  // Fetch protein data
  const fetchProteins = async (pageNum: number = 1) => {
    setIsLoading(true);
    try {

      let accessionsFilter = "";
      if (epitopeParam) {
        const mappingRes = await fetch(`/api/epitope-mapping/${epitopeParam}`);
        if (mappingRes.ok) {
          const mappingData = await mappingRes.json();
          const ids = mappingData.mapping.map((m: any) => m.protein_accession);
          if (ids.length > 0) {
            accessionsFilter = ids.join(",");
          }
        }
      }

      const params = new URLSearchParams({
        protein_name: debouncedProteinNameSearch || "",
        strain: debouncedStrainSearch || "",
        antigen: antigenFilter || "",
        has_antigen: hasAntigenOnly ? "true" : "",
        location: locationFilter || "",
        function: functionFilter || "",
        cluster: clusterFilter || "",
        sort_field: sortField || "",
        sort_direction: sortDirection || "",
        page: pageNum.toString(),
        page_size: recordsPerPage.toString()
      });

      if (accessionsFilter) {
        params.append("protein_accessions", accessionsFilter);
      }

      const usePost = !!accessionsFilter;
      const queryUrl = usePost
        ? "/api/get-comprehensive-protein-data"   // ✅ ไม่มี params ต่อท้าย
        : `/api/get-comprehensive-protein-data?${params.toString()}`;

      const response = await fetch(queryUrl, {
        method: usePost ? "POST" : "GET",
        headers: { "Content-Type": "application/json" },
        ...(usePost
          ? {
              body: JSON.stringify({
                protein_name: debouncedProteinNameSearch || "",
                strain: debouncedStrainSearch || "",
                antigen: antigenFilter || "",
                has_antigen: hasAntigenOnly ? "true" : "",
                location: locationFilter || "",
                function: functionFilter || "",
                cluster: clusterFilter || "",
                sort_field: sortField || "",
                sort_direction: sortDirection || "",
                page: pageNum,
                page_size: recordsPerPage,
                protein_accessions: accessionsFilter
                  ? accessionsFilter.split(",")
                  : [],
              }),
            }
          : {}),
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.data) {
          // Server-side pagination response
          setFilteredProteins(result.data);
          setTotalRecords(result.pagination.total_count);
          setCurrentPage(result.pagination.page);
        } else {
          // Fallback for old API format
          setFilteredProteins(result);
          setTotalRecords(result.length);
        }
      } else {

        console.error("Failed to fetch protein list", {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        });

        try {
          const errText = await response.text();
          console.error("Response body (raw):", errText);
        } catch (e) {
          console.error("Could not read error response:", e);
        }

      }
    } catch (error) {
      console.error("Error fetching proteins:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search terms
  const [debouncedProteinNameSearch, setDebouncedProteinNameSearch] = useState(proteinNameSearch);
  const [debouncedStrainSearch, setDebouncedStrainSearch] = useState(strainSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProteinNameSearch(proteinNameSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [proteinNameSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStrainSearch(strainSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [strainSearch]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchFunctionOptions(aspectFilter);
  }, [aspectFilter]);

  useEffect(() => {
    if (!initialFilterLoaded) {
      const strainParam = searchParams.get('strain');
      const clusterParam = searchParams.get('cluster');
    
      if (strainParam) setStrainSearch(strainParam);
      if (clusterParam) setClusterFilter(clusterParam);

      setInitialFilterLoaded(true);
    }
  }, [searchParams, initialFilterLoaded]);

  useEffect(() => {
    if (initialFilterLoaded) {
      fetchProteins(currentPage);
    }
  }, [
    currentPage,
    recordsPerPage,
    debouncedProteinNameSearch,
    debouncedStrainSearch,
    antigenFilter,
    hasAntigenOnly,
    locationFilter,
    functionFilter,
    clusterFilter,
    sortField,
    sortDirection,
    initialFilterLoaded,
  ]);

  // Reset page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedProteinNameSearch, debouncedStrainSearch, antigenFilter, hasAntigenOnly, locationFilter, functionFilter, clusterFilter, sortField, sortDirection]);

  // Sorting logic
  const handleSort = (field: keyof ProteinData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Use server-side sorting and pagination - data already sorted by backend
  const currentRecords = filteredProteins;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: keyof ProteinData }) => {
    if (sortField !== field) return <FaSort className="inline ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  const handleExportCSV = () => {
    const csvData = currentRecords.map(item => ({
      "GenBank Accession": item.GenBank_Accession,
      "Strain/Isolate Name": item.Strain_Isolate_Name || "-",
      "Collection Date": item.Sample_Collection_Date || "-",
      "Collection Location": item.Sample_Collection_Location || "-",
      "Sequence Length": item.Sequence_Length ?? "-",
      "Isolate Source": item.Isolate_Source || "-",
      "PubMed ID": item.PubMed_ID || "-",
      "Protein Count": item.protein_count ?? 0,
    }));

    exportToCSVWithHeaders(
      csvData,
      `asfv-genome-${new Date().toISOString().split('T')[0]}`
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Head>
        <title>Protein Database - ASFV-EpiBase</title>
        <meta name="description" content="Browse and search protein data with comprehensive annotations including location, function, and structure information" />
      </Head>

      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Protein Database
          {strainSearch && (
            <span className="ml-4 text-base font-normal text-gray-600 bg-blue-100 px-3 py-1 rounded-full inline-flex items-center">
              Strain: {strainSearch}
              <button
                onClick={() => setStrainSearch("")}
                className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                title="Clear strain filter"
              >
                ✕
              </button>
            </span>
          )}
          {clusterFilter && (
            <span className="ml-4 text-base font-normal text-gray-600 bg-purple-100 px-3 py-1 rounded-full inline-flex items-center">
              Cluster: {clusterFilter}
              <button
                onClick={() => setClusterFilter("")}
                className="ml-2 text-purple-600 hover:text-purple-800 font-bold"
                title="Clear cluster filter"
              >
                ✕
              </button>
            </span>
          )}
          {epitopeParam && (
            <span className="ml-4 text-base font-normal text-gray-600 bg-green-100 px-3 py-1 rounded-full inline-flex items-center">
              Epitope: {epitopeParam}
              <button
                onClick={() => window.location.href = "/protein"} // รีเซ็ตกลับหน้า protein หลัก
                className="ml-2 text-green-600 hover:text-green-800 font-bold"
                title="Clear epitope filter"
              >
                ✕
              </button>
            </span>
          )}
        </h1>
        <p className="text-gray-600">Browse and search protein data with comprehensive annotations including location, function, and structure information</p>
      </div>

      {/* Search Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg border mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Search & Filter</h2>
        
        {/* Search Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Protein Name / ID</label>
            <input
              type="text"
              placeholder="Enter protein name or ID..."
              value={proteinNameSearch}
              onChange={(e) => setProteinNameSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Strain</label>
            <input
              type="text"
              placeholder="Enter strain name..."
              value={strainSearch}
              onChange={(e) => setStrainSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Filter Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Antigen ({uniqueAntigens.length})
            </label>
            <select
              value={antigenFilter}
              onChange={(e) => setAntigenFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Filter by Antigen"
              disabled={filterOptionsLoading}
            >
              <option value="">All Antigens</option>
              {filterOptionsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                uniqueAntigens.map(antigen => (
                  <option key={antigen} value={antigen}>{antigen}</option>
                ))
              )}
            </select>
            
            {/* Checkbox to filter only proteins with Antigen */}
            <div className="mt-2">
              <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasAntigenOnly}
                  onChange={(e) => setHasAntigenOnly(e.target.checked)}
                  className="mr-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Show antigen only
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Location ({uniqueLocations.length})
            </label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Filter by Location"
              disabled={filterOptionsLoading}
            >
              <option value="">All Locations</option>
              {filterOptionsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                uniqueLocations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Aspect
            </label>
            <select
              value={aspectFilter}
              onChange={(e) => setAspectFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {aspectOptions.map(aspect => (
                <option key={aspect} value={aspect}>{aspect}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Function ({uniqueFunctions.length})
            </label>
            <select
              value={functionFilter}
              onChange={(e) => setFunctionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={filterOptionsLoading}
            >
              <option value="">All Functions</option>
              {filterOptionsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                uniqueFunctions.map(func => (
                  <option key={func} value={func}>{func}</option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-sm text-gray-700 mb-4">
        {totalRecords} Records Found
        {(proteinNameSearch || strainSearch || antigenFilter || hasAntigenOnly || locationFilter || functionFilter) && (
          <span className="ml-2 text-blue-600">
            (filtered)
          </span>
        )}
      </div>

      {/* Export Button and Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm"
          title="Export to CSV"
        >
          <FaDownload className="w-4 h-4" />
          Export CSV
        </button>
        
        <div className="flex items-center mb-2 sm:mb-0">
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="mr-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="First page"
          >
            <FaAngleDoubleLeft />
          </button>
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="mr-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Previous page"
          >
            <FaAngleLeft />
          </button>
          <span className="mx-2">Page</span>
          <input
            type="text"
            className="border rounded px-2 py-1 w-12 sm:w-20 text-center"
            value={currentPage}
            title="Current page number"
            placeholder="Page"
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (!isNaN(page) && page > 0 && page <= totalPages) {
                setCurrentPage(page);
              }
            }}
          />
          <span className="mx-2">of {totalPages}</span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Next page"
          >
            <FaAngleRight />
          </button>
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="ml-1 px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
            title="Last page"
          >
            <FaAngleDoubleRight />
          </button>
        </div>

        <div className="flex items-center">
          <select
            value={recordsPerPage}
            onChange={handleRecordsPerPageChange}
            className="border rounded px-2 py-1"
            title="Records per page"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="ml-2">records per page</span>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("protein_accession")}>
                Protein ID <SortIcon field="protein_accession" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("protein_name")}>
                Protein Name <SortIcon field="protein_name" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("strain")}>
                Strain <SortIcon field="strain" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("Antigen_Name")}>
                Antigen <SortIcon field="Antigen_Name" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("location_name")}>
                Location <SortIcon field="location_name" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("cellular_component")}>
                Cellular Component <SortIcon field="cellular_component" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("biological_process")}>
                Biological Process <SortIcon field="biological_process" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("molecular_function")}>
                Molecular Function <SortIcon field="molecular_function" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("pdb_id")}>
                PDB <SortIcon field="pdb_id" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("predicted_structure")}>
                Predicted Structure <SortIcon field="predicted_structure" />
              </th>
              <th className="px-2 py-2 text-left cursor-pointer" onClick={() => handleSort("cluster_id")}>
                Cluster <SortIcon field="cluster_id" />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <LoadingTable message="Loading protein data..." />
            ) : currentRecords.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-8 text-center text-gray-500 italic">
                  No data available.
                </td>
              </tr>
            ) : (
              currentRecords.map((protein, index) => (
                <tr key={`${protein.protein_accession}-${index}`} className="border-b">
                  <td className="px-2 py-2">
                    <Link href={`/protein_web/${protein.protein_accession}`} className="text-blue-600 hover:underline">
                      {protein.protein_accession}
                    </Link>
                  </td>
                  <td className="px-2 py-2">{protein.protein_name || "-"}</td>
                  <td className="px-2 py-2">{protein.strain || "-"}</td>
                  <td className="px-2 py-2 text-center">
                    <input 
                      type="checkbox" 
                      checked={protein.Antigen_Status === "มี" || protein.Antigen_Status === "Yes" || protein.Antigen_Status === "Available"} 
                      readOnly 
                      title={`Antigen: ${protein.Antigen_Name || "N/A"}`}
                      aria-label={`Antigen status: ${protein.Antigen_Status || "Not Available"}`}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-2 py-2">{protein.location_name || "-"}</td>
                  <td className="px-2 py-2">{protein.cellular_component || "-"}</td>
                  <td className="px-2 py-2">{protein.biological_process || "-"}</td>
                  <td className="px-2 py-2">{protein.molecular_function || "-"}</td>
                  <td className="px-2 py-2">{protein.pdb_id || "-"}</td>
                  <td className="px-2 py-2">{protein.predicted_structure || "-"}</td>
                  <td className="px-2 py-2">
                    {protein.cluster_id ? (
                      <button
                        onClick={() => setClusterFilter(protein.cluster_id || "")}
                        className="text-blue-500 hover:underline hover:text-blue-600 transition cursor-pointer"
                        title={`Filter by cluster ${protein.cluster_id}`}
                      >
                        {protein.cluster_id}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProteinPage;